import React, { useContext, useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, Grid, Box, CardActionArea, CardActions, Tooltip, IconButton, Avatar } from '@material-ui/core';
import PartnerStakeStyle from './PartnerStakeStyle';
import WalletContext from '../../../store/Wallet/WalletContext';
import web3Helper from '../../../services/web3';
import { MdOutlineError } from 'react-icons/md';
import { useWalletConnect, useWeb3 } from '../../../services/web3/hooks/useWeb3';
import { getChainName, getExplorerUrl } from '../../../services/web3/constants/chains';
import AlertContext from '../../../store/Alert/AlertContext';
import { switchChain } from "@wagmi/core";
import { useAccount, useConfig } from 'wagmi';
import formatDuration from '../../../helpers/MomentDuration';
import { FaInfoCircle } from 'react-icons/fa';
import AuthContext from '../../../store/Auth/AuthContext';
import metamask from '../../../assets/images/metamaskIcon.svg';

const MetaIcon = () => {
  return <img src={metamask} alt="" className="img-fluid img-icon" />;
};

const pluralize = (value: number, unit: string) => `${value} ${unit}${value > 1 ? 's' : ''}`;

const PartnerStakeDetailCard: React.FC<any> = ({ pool, stakeConfig, tokenName, toggleView, hasStaked, toggleHasStaked, totalStaked, totalStakedUser, totalStakedError, showWithdraw, maxStakeDuration }) => {
  
  // TODO: Stake Button disabled states
  // Date should be in future
  // Should not have already staked
  // Total staked should be less than maxCap
  // TODO: Withdrawal Button
  const classes = PartnerStakeStyle();

  const config = useConfig();

  const { setAlert } = useContext<any>(AlertContext);

  const { openConnectModal } = useWalletConnect();
  const { isAuth } = useContext<any>(AuthContext);
  const { isConnected } = useWeb3();
  const { openAccountModal } = useWalletConnect();
  const { chainId } = useAccount({ config })
  
  const [explorerUrl, setExplorerUrl] = useState<string | null>();
  const [highestYield, setHighestYield] = useState(0);
  const [formattedMaxDuration, setFormattedMaxDuration] = useState('');
  const [tipText, setTipText] = useState('');

  useEffect(() => {
    let explorerUrl = getExplorerUrl(stakeConfig.networkId);
    if (explorerUrl)
    setExplorerUrl(explorerUrl + '/address/' + stakeConfig.poolStakeAddress);
    setHighestYield(getHighestYield(stakeConfig.withdrawalRules));
    const minDuration = getLowestYieldDuration(stakeConfig.withdrawalRules);
    const isEarlyWuthdrawalEnabled = stakeConfig.earlyWithdrawalPenalty;
    createTipText(minDuration, isEarlyWuthdrawalEnabled);
    const maxDuration = getHighestYieldDuration(stakeConfig.withdrawalRules);
    const [maxValue, maxUnit] = formatDuration(maxDuration);
    const pluralizedUnit = pluralize(maxValue, maxUnit);
    setFormattedMaxDuration(pluralizedUnit);
  }, [])

  const isStakeOpen = (stakeDeadline: Date) => {
    let today = new Date();
    return today < new Date(stakeDeadline);
  }

  const getHighestYield = (withdrawalRules: any[]) => {
    return Math.max(...withdrawalRules.map(rule => rule.apy));
  }

  const getHighestYieldDuration = (withdrawalRules: any[]) => {
    return Math.max(
      ...withdrawalRules.map((rule) => Number(rule.duration))
    );
  }

  const getLowestYieldDuration = (withdrawalRules: any[]) => {
    return Math.min(
      ...withdrawalRules.map((rule) => Number(rule.duration))
    );
  }
  const handleWallet = () => {
    if (!isConnected && openConnectModal) {
      openConnectModal()
    }
  }

  const switchNetwork = () => {
    switchChain(config, { 
      chainId: stakeConfig.networkId
    })
    .then(() => {
      console.info("Successfully switched network")
    })
    .catch((error: any) => {
      setAlert('Unable to switch network, please switch network manually on the wallet to continue', 'error');
    })
  }

  const handleAvatarClick = () => {
    if (openAccountModal) openAccountModal();
  };

  const createTipText = (minDuration: number, isEarlyWithdrawalEnabled: boolean) => {
    let tipText: string;
    const [value, unit] = formatDuration(minDuration);
    const formattedDuration = pluralize(value, unit);
    if (isEarlyWithdrawalEnabled) {
      tipText = `In this staking pool, early unstaking is allowed, enabling users to withdraw their stake before the minimum duration of ${formattedDuration}. However, a penalty will be applied. After the minimum duration, users can unstake freely according to the maturity details.`;
    } else {
      tipText = `Early unstaking is not allowed in this pool. Users must wait until the minimum staking duration of ${formattedDuration}, after which they can unstake according to the maturity details.`;
    }
    setTipText(tipText);
  }

  return (
    <Card className={classes.cardRoot}>
      <CardContent>
        {/* <Typography variant="subtitle1" gutterBottom className={classes.title}>
          Unlock the power of your tokens
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom className={classes.description}>
          {formatDuration(maxStakeDuration)} of high-yield farm for {pool?.name}. Stake now to earn.
        </Typography> */}
        
        <Grid
          container
          direction="column"
          className={classes.detailContainer}
        >
          <Grid item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item>
                <Typography>
                  Duration
                </Typography>
              </Grid>
              <Grid item xs={true}>
                <Box border={"1px #ECF0F3 dashed"} marginX={"18px"}></Box>
              </Grid>
              <Grid item>
                <Typography>
                  <span className="detailValue">{formattedMaxDuration}</span>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item>
                <Typography>
                  Yield
                </Typography>
              </Grid>
              <Grid item xs={true}>
                <Box border={"1px #ECF0F3 dashed"} marginX={"18px"}></Box>
              </Grid>
              <Grid item>
                <Typography>
                  <span className="detailValue">{highestYield}%</span>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item>
                <Typography>
                  Network
                </Typography>
              </Grid>
              <Grid item xs={true}>
                <Box border={"1px #ECF0F3 dashed"} marginX={"18px"}></Box>
              </Grid>
              <Grid item>
                <Typography>
                  <span className="detailValue">{getChainName(stakeConfig.networkId)}</span>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item>
                <Typography>
                  Staked
                </Typography>
              </Grid>
              <Grid item xs={true}>
                <Box border={"1px #ECF0F3 dashed"} marginX={"18px"}></Box>
              </Grid>
              <Grid item>
                <Typography>
                  <span className="detailValue">{ hasStaked ? totalStakedUser + ` ${tokenName}` : "N/A" }</span>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CardActionArea className={classes.cardAction}>
          {
            isConnected && !isAuth && (
              <IconButton
                aria-label="wallet"
                onClick={handleAvatarClick}
                style={{ position: 'absolute', top: '-6px', right: '5px', zIndex: 2 }}
                >
                  <Avatar alt="User Wallet" style={{ width: 28, height: 28, backgroundColor: 'white' }}>
                    <MetaIcon />
                  </Avatar>
                </IconButton>
            )
          }
          {
            isConnected
              ?
                isConnected && chainId !== stakeConfig.networkId
                ?
                  <Button variant="contained" color="primary" fullWidth className={classes.cardActionButton} onClick={() => switchNetwork()}>
                    Switch Network
                  </Button>
                : hasStaked
                  ?
                    <Button variant="contained" color="primary" fullWidth className={classes.cardActionButton} onClick={() => showWithdraw()}>
                      Withdraw
                    </Button>
                  :
                    <Button variant="contained" color="primary" fullWidth className={classes.cardActionButton} onClick={() => toggleView()} disabled={!isStakeOpen(stakeConfig.stakingDeadline)}>
                      Stake Now
                    </Button>
              :
                <Button variant="contained" color="secondary" fullWidth className={classes.cardActionButton} onClick={() => handleWallet()}>
                  Connect Wallet →
                </Button>
          }
        </CardActionArea>
      </CardContent>
      <CardActions className={classes.stakeDetailContainer}>
        <Grid item sm={12}>
          <Grid
              container
              direction="column"
              className={classes.stakeDetailsFooter}
            >
              <Grid item>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <Typography>
                      {
                        isStakeOpen(stakeConfig.stakingDeadline) ? "Closes" : "Closed"
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={true}>
                    <Box border={"1px #8CA2B7 dashed"} marginX={"18px"}></Box>
                  </Grid>
                  <Grid item>
                    <Typography>
                      <span className="detailValue">{new Date(stakeConfig.stakingDeadline).toLocaleDateString()}</span>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <Typography>
                      Total Staked
                    </Typography>
                  </Grid>
                  <Grid item xs={true}>
                    <Box border={"1px #8CA2B7 dashed"} marginX={"18px"}></Box>
                  </Grid>
                  <Grid item>
                    <Typography>
                      <span className="detailValue">
                        {
                          totalStakedError
                            ? <MdOutlineError color='red' />
                            : <Typography>{totalStaked} <span className={classes.token}>{tokenName}</span></Typography>
                        }
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <Typography>
                      Contract
                    </Typography>
                  </Grid>
                  <Grid item xs={true}>
                    <Box border={"1px #8CA2B7 dashed"} marginX={"18px"}></Box>
                  </Grid>
                  <Grid item>
                    <Typography>
                      <span className={classes.externalLinks}>{explorerUrl ? <a href={explorerUrl} target='_blank'>View Contract</a> : <MdOutlineError color='yellow' />}</span>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <Typography>
                      Unstaking Early
                    </Typography>
                  </Grid>
                  <Grid item xs={true}>
                    <Box border={"1px #8CA2B7 dashed"} marginX={"18px"}></Box>
                  </Grid>
                  <Grid item>
                    <Typography style={{ display: "inline-flex", alignItems: "center" }}>
                      <span className={classes.externalLinks}>
                        { stakeConfig.earlyWithdrawalPenalty ? 'Yes': 'No' }
                        </span>
                        &nbsp;
                        <Tooltip disableFocusListener title={tipText}>
                          <span style={{ display: "inline-flex", alignItems: "center" }}><FaInfoCircle style={{ cursor: 'pointer' }} /></span>
                        </Tooltip>
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
          </Grid>
        </Grid>
        <Grid item sm={12}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            spacing={3}
            style={{
              marginBottom: '5px',
            }}
          >
            {stakeConfig.withdrawalRules.length > 1 &&
              stakeConfig.withdrawalRules
                .sort((a: any, b: any) => Number(a.apy) - Number(b.apy)) // Sort in increasing APY
                .map((rule: any, index: any) => {
                  const isHighestYield = Number(rule.apy) === highestYield;
                  const [lowerValue, lowerUnit] = formatDuration(Number(rule.duration)); // Get the current duration value and unit
                  const nextRule = stakeConfig.withdrawalRules[index + 1]; // Get the next rule for the range

                  let durationText;

                  // If it's the last rule, show "After X days/months/years"
                  if (!nextRule) {
                    durationText = `After ${pluralize(lowerValue, lowerUnit)}`;
                  } else {
                    const [upperValue, upperUnit] = formatDuration(Number(nextRule.duration), lowerUnit);
                    durationText = `${lowerValue} to ${pluralize(upperValue, lowerUnit)}`;
                  }

                  return (
                    <Grid
                      item
                      xs={4}
                      key={index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '70px', // Set fixed height for consistency
                      }}
                    >
                      {/* Duration box (black background) */}
                      <Box
                        style={{
                          backgroundColor: '#000',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px 4px 0 0', // Rounded top corners
                          width: '100%', // Make the width 100% of the parent Box
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography variant="caption" className="font-normal">
                          {durationText}
                        </Typography>
                      </Box>

                      {/* APY box (gray background or primary-blue for highest) */}
                      <Box
                        style={{
                          backgroundColor: isHighestYield ? '#3EB8AA' : '#8CA2B7',
                          color: '#fff',
                          padding: '4px 8px',
                          borderRadius: '0 0 4px 4px', // Rounded bottom corners
                          width: '100%', // Make the width 100% of the parent Box
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="subtitle2" className="font-bold">
                          {`${rule.apy}% APR`}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
};

export default PartnerStakeDetailCard;
